import { useCallback, useEffect, useState } from 'react';

import { getErrorMessage, getPrograms, Program } from '@/lib/api/program';
import { DEFAULT_SUPPLIER } from '@/lib/constants/suppliers';
import { getStartOfDayInUTC } from '@/lib/date';

/**
 * Custom hook for program data management
 * Handles fetching and managing program list state
 *
 * @param initialDate Initial date
 * @param initialSupplier Initial supplier
 */
export const useProgramData = (
  initialDate: Date | null = new Date(),
  initialSupplier: string = DEFAULT_SUPPLIER,
) => {
  // Program list
  const [programs, setPrograms] = useState<Program[]>([]);
  // Loading state
  const [loading, setLoading] = useState(true);
  // Error state
  const [error, setError] = useState<string | null>(null);
  // Selected date
  const [selectedDate, setSelectedDate] = useState<Date | null>(initialDate);
  // Selected supplier
  const [supplier, setSupplier] = useState<string>(initialSupplier);

  // Function to fetch program list
  const fetchPrograms = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // In normal mode, fetch programs for the selected date
      if (!selectedDate) return;

      // Create UTC datetime string representing 00:00 in local timezone for the selected date
      const startAt = getStartOfDayInUTC(selectedDate);

      // Fetch program list from API
      const response = await getPrograms(startAt, undefined, supplier || undefined);
      setPrograms(response.data || []);
    } catch (err) {
      console.error('Failed to fetch programs:', err);
      // Convert error message to user-friendly format
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [selectedDate, supplier]);

  // Initial data fetch and refetch on filter changes
  useEffect(() => {
    fetchPrograms();
  }, [selectedDate, supplier, fetchPrograms]);

  // Date change handler
  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
  };

  // Supplier change handler
  const handleSupplierChange = (value: string) => {
    setSupplier(value);
  };

  return {
    // State
    programs,
    loading,
    error,
    selectedDate,
    supplier,

    // Actions
    setSelectedDate,
    setSupplier,
    handleDateChange,
    handleSupplierChange,
    refreshData: fetchPrograms,
  };
};

export default useProgramData;
